import { Card } from '@heroui/react';

import { JUDGEMENTS } from '../../../i18n/el';

// Σύνοψη των κρίσεων της συλλογής (ΛΑ-16, ΛΑ-17), στην πλαϊνή στήλη.
//
// **Χωρίς δικό της αίτημα.** Τα πλήθη έρχονται ήδη με κάθε κάρτα πόρου, από την
// ίδια διαδρομή που φορτώνει η οθόνη: το API τα υπολογίζει ανά συλλογή για την
// προειδοποίηση της **D13**. Δεύτερο αίτημα για να αθροιστούν αριθμοί που
// βρίσκονται ήδη στη μνήμη θα ήταν καθαρή σπατάλη — και θα απαιτούσε νέα διαδρομή
// εκτός του πίνακα του §4.5.1.
//
// Η κάρτα δείχνει **πού** υπάρχει συζήτηση, όχι το περιεχόμενό της: τα σχόλια
// ανήκουν στον πόρο (ΛΑ-16: «σχόλια σε έναν πόρο») και διαβάζονται από την κάρτα
// του, όπου υπάρχει και το πλαίσιο.

/** Το προσβάσιμο κείμενο των δύο πληθών, όπου το εικονίδιο δεν υπάρχει. */
const countsLabel = (resource) =>
    `${resource.commentCount ?? 0} ${JUDGEMENTS.comments}, ` +
    `${resource.ratingCount ?? 0} ${JUDGEMENTS.ratings}`;

export default function CollectionJudgements({ resources = [], onOpenResource }) {
    // **Δύο λίστες και όχι μία.** Η ενιαία λίστα ταξινομούσε με το **άθροισμα** των
    // δύο πληθών, οπότε ένας πόρος με πέντε σχόλια και καμία αξιολόγηση και ένας με
    // πέντε αξιολογήσεις και κανένα σχόλιο έβγαιναν στην ίδια θέση με το ίδιο
    // νούμερο δίπλα τους. Το άθροισμα δύο πραγμάτων που δεν είναι το ίδιο πράγμα
    // δεν απαντά σε καμία ερώτηση: ούτε «πού συζητούν» ούτε «τι έχει κριθεί».
    const topBy = (key) =>
        resources
            .filter((resource) => (resource[key] ?? 0) > 0)
            .sort((a, b) => (b[key] ?? 0) - (a[key] ?? 0))
            .slice(0, 4);

    const groups = [
        {
            key: 'ratingCount',
            // Η όψη στην οποία προσγειώνεται ο διάλογος. Ζει **δίπλα στη λίστα**
            // και όχι στον καλούντα: η λίστα ξέρει τι μετρά, ο καλών όχι.
            pane: 'ratings',
            title: JUDGEMENTS.summaryTopRatings,
            icon: 'fa-solid fa-star-half-stroke',
            items: topBy('ratingCount')
        },
        {
            key: 'commentCount',
            pane: 'comments',
            title: JUDGEMENTS.summaryTopComments,
            icon: 'fa-solid fa-comment',
            items: topBy('commentCount')
        }
    ];

    const totals = resources.reduce(
        (sum, resource) => ({
            comments: sum.comments + (resource.commentCount ?? 0),
            ratings: sum.ratings + (resource.ratingCount ?? 0)
        }),
        { comments: 0, ratings: 0 }
    );

    const isEmpty = groups.every((group) => group.items.length === 0);

    return (
        <Card>
            <Card.Header>
                <Card.Title>{JUDGEMENTS.summaryTitle}</Card.Title>
            </Card.Header>
            <Card.Content>
                {isEmpty ? (
                    <p className="text-sm text-muted">{JUDGEMENTS.summaryEmpty}</p>
                ) : (
                    <div className="flex flex-col gap-5">
                        {/* Τα δύο σύνολα, το καθένα με το εικονίδιό του και το όνομά
                            του. Ήταν μία γραμμή «Σχόλια: 4 · Αξιολογήσεις: 2», που
                            διαβαζόταν ως ένα μέγεθος με δύο μέρη. */}
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted">
                            <span className="flex items-center gap-1.5">
                                <i className="fa-solid fa-star-half-stroke" aria-hidden="true" />
                                {`${JUDGEMENTS.ratings}: ${totals.ratings}`}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <i className="fa-solid fa-comment" aria-hidden="true" />
                                {`${JUDGEMENTS.comments}: ${totals.comments}`}
                            </span>
                        </div>

                        {groups.filter((group) => group.items.length > 0).map((group) => (
                            <div key={group.key} className="flex flex-col gap-2">
                                <h4 className="text-sm font-semibold">{group.title}</h4>
                                <ul className="flex flex-col gap-2">
                                    {group.items.map((resource) => (
                                        <li key={resource.id}>
                                            {/* Β4α: ο τίτλος **ανοίγει τον πόρο**. Ήταν αδρανές
                                                κείμενο — η κάρτα έδειχνε πού υπάρχει συζήτηση
                                                χωρίς να δίνει τρόπο να πάει κανείς εκεί, οπότε ο
                                                χρήστης έψαχνε τον πόρο στη λίστα με το μάτι. */}
                                            <button
                                                type="button"
                                                onClick={() => onOpenResource?.(resource.id, group.pane)}
                                                className="flex w-full items-baseline justify-between gap-3 rounded-lg text-left hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                                            >
                                                <span className="truncate text-sm">{resource.title}</span>

                                                {/* Β4β: το πλήθος είχε μορφή «2 · 2» — δύο αριθμοί
                                                    που δεν λένε τι μετρούν. Τώρα κάθε λίστα δείχνει
                                                    **ένα** μέγεθος, το δικό της, και το προσβάσιμο
                                                    κείμενο το ονομάζει για τον αναγνώστη οθόνης
                                                    (ΜΛΑ-11: καμία πληροφορία μόνο με σχήμα). */}
                                                <span
                                                    className="flex shrink-0 items-center gap-1 text-xs text-muted"
                                                    aria-label={countsLabel(resource)}
                                                >
                                                    <i className={group.icon} aria-hidden="true" />
                                                    {resource[group.key] ?? 0}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </Card.Content>
        </Card>
    );
}
